import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import api from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const validateAndSetToken = useCallback(async (accessToken, refreshToken) => {
    try {
      // Set token in axios defaults
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      setToken(accessToken);
      
      // Try to fetch user to validate token
      const response = await api.get('/users/users/me/');
      setUser(response.data);
      setLoading(false);
    } catch (error) {

      
      // If 401, try to refresh token
      if (error.response?.status === 401 && refreshToken) {
        try {
          const refreshResponse = await axios.post(
            `${api.defaults.baseURL}/auth/token/refresh/`,
            { refresh: refreshToken }
          );
          const { access } = refreshResponse.data;
          
          // Save new token and retry
          localStorage.setItem('token', access);
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          setToken(access);
          
          // Retry fetching user
          const userResponse = await api.get('/users/users/me/');
          setUser(userResponse.data);
          setLoading(false);
        } catch (refreshError) {

          // Refresh failed, clear everything
          clearAuth();
          setLoading(false);
        }
      } else {
        // Other errors or no refresh token - clear auth
        clearAuth();
        setLoading(false);
      }
    }
  }, [clearAuth]);

  useEffect(() => {
    // Validate and set token on app load
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    
    if (storedToken && storedRefreshToken) {
      // Validate token by trying to fetch user
      validateAndSetToken(storedToken, storedRefreshToken);
    } else {
      // No tokens, clear everything and set loading to false
      clearAuth();
      setLoading(false);
    }
  }, [validateAndSetToken, clearAuth]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/users/users/me/');
      setUser(response.data);
    } catch (error) {

      // Only logout on 401 (Unauthorized) - means token is invalid
      if (error.response?.status === 401) {
        clearAuth();
      }
    }
  }, [clearAuth]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login/', { email, password });
      
      const { tokens, user } = response.data;
      
      if (!tokens || !user) {

        return {
          success: false,
          error: 'Invalid response from server'
        };
      }
      
      localStorage.setItem('token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);
      setToken(tokens.access);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
      setUser(user);

      // Mark tutorial as seen for existing users logging in
      // (tutorial should only show for brand-new registrations)
      if (user?.id) {
        localStorage.setItem(`pop1_tutorial_seen_${user.id}`, 'true');
      }

      return { success: true };
    } catch (error) {


      // Check if it's a network error (backend not running)
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK' || !error.response) {
        return {
          success: false,
          error: 'Cannot connect to server. Please make sure the backend server is running on port 8000.'
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/users/users/', userData);
      const { tokens, user } = response.data;
      
      localStorage.setItem('token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);
      setToken(tokens.access);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
      setUser(user);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await api.post('/auth/logout/', { refresh_token: refreshToken });
      }
    } catch (error) {

    } finally {
      clearAuth();
    }
  };

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email, username: user.username });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

